import React, { useEffect, useRef, useState } from 'react'
import GradientButton from '../components/GradientButton'
import { useNavigate } from 'react-router-dom'
import { ensureOfflineReady } from '../offline/initOffline'
import { dexieFarmRepo } from '../offline/repos/DexieFarmRepo'
import type { EntityBatchSyncStatus, EntityMutationSyncState } from '../utils/entityMutationSync'
import { readCurrentFarm } from '../utils/farmWizard'
import {
  readProductionUnitWizardSyncStatus,
  retryProductionUnitWizardSyncStatusWithReport,
  saveProductionUnits,
  type ProductionUnitRecord,
  type ProductionUnitWizardSyncTarget,
  type ProductionUnitWizardMutationResult
} from '../utils/productionUnitWizardApi'
import { deriveProductionUnitSyncOutcome } from '../utils/productionUnitSyncOutcome'
import { generateUuid } from '../utils/uuid'

const UNIT_TYPES = ['Pond', 'Tank', 'RAS', 'Cage', 'Other']
const CAP_UNITS = ['m3', 'ha', 'units', 'L']

type ProductionUnitMutationVerb = 'saved' | 'removed'

const getProductionUnitStatusMessage = (
  verb: ProductionUnitMutationVerb,
  state: EntityMutationSyncState
): string => {
  if (verb === 'saved') {
    if (state === 'synced') return 'Production unit saved and synced.'
    if (state === 'offline') return 'Production unit saved locally while offline. It will sync when you reconnect.'
    return 'Production unit saved locally. Server sync is pending.'
  }

  if (state === 'synced') return 'Production unit removed and synced.'
  if (state === 'offline') return 'Production unit removed locally while offline. It will sync when you reconnect.'
  return 'Production unit removed locally. Server sync is pending.'
}

function loadCurrentFarm() {
  return (readCurrentFarm() ?? {}) as any
}

export default function ProductionUnit() {
  const [units, setUnits] = useState<ProductionUnitRecord[]>([])
  const [name, setName] = useState('')
  const [type, setType] = useState(UNIT_TYPES[0])
  const [otherType, setOtherType] = useState('')
  const [capacity, setCapacity] = useState('')
  const [capacityUnit, setCapacityUnit] = useState(CAP_UNITS[0])
  const [persisting, setPersisting] = useState(false)
  const [retryingSync, setRetryingSync] = useState(false)
  const [statusMessage, setStatusMessage] = useState<{ tone: 'info' | 'success' | 'error'; message: string } | null>(null)
  const [syncStatus, setSyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  const [syncLastOutcome, setSyncLastOutcome] = useState<{ resultLabel: string; attemptedAtIso: string } | null>(null)
  const [selectedFarmId, setSelectedFarmId] = useState('')
  const statusVersionRef = useRef(0)
  const navigate = useNavigate()

  const syncTarget: ProductionUnitWizardSyncTarget = {
    farmId: selectedFarmId,
    productionUnitIds: units.map((unit) => unit.id).filter(Boolean)
  }

  const syncAttentionMessage = (() => {
    if (!syncStatus || syncStatus.syncState === 'synced') return null

    if (syncStatus.blockedCount > 0) {
      if (syncStatus.errorMessage) {
        return `${syncStatus.blockedCount} production unit change(s) are blocked: ${syncStatus.errorMessage}`
      }
      return `${syncStatus.blockedCount} production unit change(s) are blocked.`
    }

    if (syncStatus.syncState === 'offline') {
      return 'Production unit changes are queued locally while this device is offline.'
    }

    return 'Production unit changes are queued and waiting to sync.'
  })()

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const current = loadCurrentFarm()
      const farmId = typeof current?.id === 'string' ? current.id : ''
      if (!cancelled) setSelectedFarmId(farmId)

      if (!farmId) {
        if (!cancelled) setUnits(current.productionUnits || [])
        return
      }

      await ensureOfflineReady()
      const fromDb = await dexieFarmRepo.getById(farmId)
      const resolved = fromDb ?? current
      if (!cancelled) setUnits(resolved.productionUnits || [])
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let active = true

    void (async () => {
      if (!syncTarget.farmId) {
        if (active) setSyncStatus(null)
        return
      }

      const status = await readProductionUnitWizardSyncStatus(syncTarget)
      if (!active) return
      setSyncStatus(status)
    })()

    return () => {
      active = false
    }
  }, [syncTarget.farmId, units])

  const refreshUnits = async (): Promise<void> => {
    const current = loadCurrentFarm()
    const farmId = typeof current?.id === 'string' ? current.id : ''
    if (!farmId) return

    await ensureOfflineReady()
    const fromDb = await dexieFarmRepo.getById(farmId)
    const resolved = fromDb ?? current
    setUnits(Array.isArray(resolved?.productionUnits) ? resolved.productionUnits : [])

    const status = await readProductionUnitWizardSyncStatus({
      farmId,
      productionUnitIds: (Array.isArray(resolved?.productionUnits) ? resolved.productionUnits : [])
        .map((unit: ProductionUnitRecord) => unit.id)
        .filter(Boolean)
    })
    setSyncStatus(status)
    setSyncLastOutcome({
      resultLabel: deriveProductionUnitSyncOutcome({ status, skippedNotFoundCount: 0 }).resultLabel,
      attemptedAtIso: new Date().toISOString()
    })
  }

  const retrySync = async (): Promise<void> => {
    if (!syncTarget.farmId) return
    setRetryingSync(true)

    try {
      const report = await retryProductionUnitWizardSyncStatusWithReport(syncTarget, {
        maxEntries: 200,
        maxPasses: 5
      })

      setSyncStatus(report.status)
      setSyncLastOutcome({
        resultLabel: deriveProductionUnitSyncOutcome({
          status: report.status,
          skippedNotFoundCount: report.skippedNotFoundCount
        }).resultLabel,
        attemptedAtIso: new Date().toISOString()
      })
      await refreshUnits()
    } catch {
      setStatusMessage({ tone: 'error', message: 'Unable to retry production unit sync right now.' })
    } finally {
      setRetryingSync(false)
    }
  }

  const applyPersistStatus = (
    verb: ProductionUnitMutationVerb,
    result: Pick<ProductionUnitWizardMutationResult, 'syncState' | 'syncPromise'>
  ) => {
    const nextVersion = statusVersionRef.current + 1
    statusVersionRef.current = nextVersion

    setStatusMessage({
      tone: result.syncState === 'synced' ? 'success' : 'info',
      message: getProductionUnitStatusMessage(verb, result.syncState)
    })

    if (!result.syncPromise) return

    void result.syncPromise.then((nextState) => {
      if (statusVersionRef.current !== nextVersion) return
      if (nextState === result.syncState) return

      setStatusMessage({
        tone: nextState === 'synced' ? 'success' : 'info',
        message: getProductionUnitStatusMessage(verb, nextState)
      })
    })
  }

  async function addUnit() {
    if (!name.trim()) return alert('Please enter a name for the production unit')

    const resolvedType = type === 'Other' ? (otherType.trim() || 'Other') : type
    const cap = capacity.trim() ? Number(capacity) : null
    const rec: ProductionUnitRecord = {
      id: generateUuid(),
      name: name.trim(),
      type: resolvedType,
      capacity: cap,
      capacityUnit,
    }
    const previousUnits = units
    const next = [...units, rec]
    setUnits(next)

    try {
      setPersisting(true)
      const result = await saveProductionUnits(next)
      applyPersistStatus('saved', result)
    } catch (error) {
      console.warn('ProductionUnit: failed to persist units', error)
      setUnits(previousUnits)
      setStatusMessage({ tone: 'error', message: 'Could not save production units. Please try again.' })
      return
    } finally {
      setPersisting(false)
    }

    setName('')
    setType(UNIT_TYPES[0])
    setOtherType('')
    setCapacity('')
    setCapacityUnit(CAP_UNITS[0])
  }

  async function removeUnit(id: string) {
    const previousUnits = units
    const next = units.filter(u => u.id !== id)
    setUnits(next)

    try {
      setPersisting(true)
      const result = await saveProductionUnits(next)
      applyPersistStatus('removed', result)
    } catch (error) {
      console.warn('ProductionUnit: failed to persist units', error)
      setUnits(previousUnits)
      setStatusMessage({ tone: 'error', message: 'Could not update production units. Please try again.' })
    } finally {
      setPersisting(false)
    }
  }

  function onNext() {
    navigate('/navegate')
  }

  const statusStyle = (tone: 'info' | 'success' | 'error'): React.CSSProperties => {
    const palette = {
      info: {
        border: '1px solid #bae6fd',
        background: '#f0f9ff',
        color: '#075985'
      },
      success: {
        border: '1px solid #86efac',
        background: '#f0fdf4',
        color: '#166534'
      },
      error: {
        border: '1px solid #fca5a5',
        background: '#fef2f2',
        color: '#b91c1c'
      }
    } as const

    return {
      marginTop: 12,
      padding: '12px 14px',
      borderRadius: 8,
      border: palette[tone].border,
      background: palette[tone].background,
      color: palette[tone].color,
      fontSize: 14,
      lineHeight: 1.5,
      fontWeight: 500
    }
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 className="h2">Production Unit</h2>
      <p className="lead">Add one or more production units used on this farm (ponds, tanks, RAS, cages, etc.).</p>

      <div className="form-card" style={{ marginTop: 12 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <label>
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pond A" />
          </label>

          <label>
            <span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {UNIT_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>

          {type === 'Other' && (
            <label>
              <span>Describe type</span>
              <input value={otherType} onChange={(e) => setOtherType(e.target.value)} placeholder="Describe other type" />
            </label>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ flex: 1 }}>
              <span>Capacity</span>
              <input
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="e.g. 1000"
                inputMode="numeric"
              />
            </label>

            <label>
              <span>Unit</span>
              <select value={capacityUnit} onChange={(e) => setCapacityUnit(e.target.value)}>
                {CAP_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <GradientButton disabled={persisting} onClick={() => void addUnit()}>
              {persisting ? 'Saving...' : 'Add Unit'}
            </GradientButton>
            <button type="button" disabled={persisting} onClick={() => { setName(''); setType(UNIT_TYPES[0]); setOtherType(''); setCapacity(''); setCapacityUnit(CAP_UNITS[0]) }}>Clear</button>
          </div>

          {statusMessage && (
            <div aria-live="polite" role="status" style={statusStyle(statusMessage.tone)}>
              {statusMessage.message}
            </div>
          )}

          {syncLastOutcome && (
            <div aria-live="polite" role="status" style={statusStyle('info')}>
              <div>Last sync result: {syncLastOutcome.resultLabel}</div>
              <div>Last attempted: {new Date(syncLastOutcome.attemptedAtIso).toLocaleString()}</div>
            </div>
          )}

          {syncAttentionMessage && (
            <div aria-live="polite" role="status" style={statusStyle('info')}>
              <div>{syncAttentionMessage}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <GradientButton
                  type="button"
                  variant="info"
                  onClick={() => void retrySync()}
                  disabled={retryingSync || persisting}
                >
                  {retryingSync ? 'Retrying sync...' : 'Retry sync'}
                </GradientButton>
                <GradientButton
                  type="button"
                  variant="secondary"
                  onClick={() => void refreshUnits()}
                  disabled={persisting || retryingSync}
                >
                  Refresh units
                </GradientButton>
              </div>
            </div>
          )}
        </div>

        <hr style={{ margin: '12px 0' }} />

        <h3 style={{ marginTop: 0 }}>Units</h3>
        {units.length === 0 && <p>No production units added yet.</p>}

        <ul>
          {units.map((u) => {
            const capacityLabel = typeof u.capacity === 'number'
              ? `· ${u.capacity} ${u.capacityUnit ?? ''}`.trim()
              : ''

            return (
              <li key={u.id} style={{ padding: 8, border: '1px solid #eee', marginBottom: 8, borderRadius: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{u.name}</strong>
                    <div style={{ fontSize: 13, color: '#444' }}>{u.type} {capacityLabel}</div>
                  </div>
                  <div>
                    <button type="button" disabled={persisting} onClick={() => void removeUnit(u.id)} style={{ marginLeft: 8 }}>Remove</button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>

        <div style={{ marginTop: 12 }}>
          <GradientButton disabled={persisting} onClick={onNext}>Next: Navegate</GradientButton>
        </div>
      </div>
    </div>
  )
}
